import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { GoogleSignInButton } from "../components/GoogleSignInButton";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { motion } from "framer-motion";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { LineMask } from "../components/marketing/Reveal";

export default function Register() {
  const [form, setForm] = useState({ name: "", email: "", password: "", clinic_name: "", country: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", form);
      setUser(data);
      toast.success("Account created — submit your first case.");
      navigate("/portal", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink text-white grid lg:grid-cols-2 noise-overlay">
      <div className="hidden lg:block relative overflow-hidden">
        <img src="https://images.unsplash.com/photo-1588776814546-daab30f310ce?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwzfHxkZW50YWwlMjBjbGluaWMlMjBwcm9mZXNzaW9uYWx8ZW58MHx8fHwxNzg3NDEzNDE1fDA&ixlib=rb-4.1.0&q=85"
          alt="Dental professionals in a clinic" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-l from-ink via-ink/50 to-ink/20" />
        <div className="absolute bottom-16 left-16 right-16">
          <p className="font-display font-black text-3xl xl:text-4xl leading-tight">An extension of<br /><span className="text-neon">your clinical team.</span></p>
        </div>
      </div>

      <div className="flex flex-col justify-center px-6 sm:px-16 lg:px-24 py-16">
        <Link to="/" className="flex items-center gap-2 mb-12" data-testid="register-logo">
          <span className="h-8 w-8 rounded-lg bg-neon flex items-center justify-center font-display font-black text-ink text-sm">OX</span>
          <span className="font-display font-bold text-lg tracking-tight">OrthoPlani<span className="text-neon">X</span></span>
        </Link>
        <h1 className="font-display font-black tracking-tight text-4xl sm:text-5xl leading-[1.0]">
          <LineMask delay={0.05}>Create your</LineMask>
          <LineMask delay={0.2} className="text-neon">professional account.</LineMask>
        </h1>
        <p className="mt-5 text-slate-400 text-sm max-w-md">For licensed dental professionals, clinics, laboratories and aligner companies.</p>

        <form onSubmit={submit} className="mt-10 space-y-5 max-w-md" data-testid="register-form">
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label htmlFor="r-name" className="text-slate-300">Full name *</Label>
              <Input id="r-name" data-testid="register-name-input" required value={form.name} onChange={set("name")}
                className="bg-ink-2/80 border-white/15 text-white placeholder:text-slate-600 focus-visible:ring-neon h-12" placeholder="Dr. Jane Smith" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="r-email" className="text-slate-300">Work email *</Label>
              <Input id="r-email" data-testid="register-email-input" type="email" required value={form.email} onChange={set("email")}
                className="bg-ink-2/80 border-white/15 text-white placeholder:text-slate-600 focus-visible:ring-neon h-12" placeholder="you@clinic.com" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="r-clinic" className="text-slate-300">Clinic / Company</Label>
              <Input id="r-clinic" data-testid="register-clinic-input" value={form.clinic_name} onChange={set("clinic_name")}
                className="bg-ink-2/80 border-white/15 text-white placeholder:text-slate-600 focus-visible:ring-neon h-12" placeholder="Smile Studio Dental" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="r-country" className="text-slate-300">Country</Label>
              <Input id="r-country" data-testid="register-country-input" value={form.country} onChange={set("country")}
                className="bg-ink-2/80 border-white/15 text-white placeholder:text-slate-600 focus-visible:ring-neon h-12" placeholder="Australia" />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="r-pass" className="text-slate-300">Password *</Label>
            <Input id="r-pass" data-testid="register-password-input" type="password" required minLength={8} autoComplete="new-password"
              value={form.password} onChange={set("password")}
              className="bg-ink-2/80 border-white/15 text-white placeholder:text-slate-600 focus-visible:ring-neon h-12" placeholder="Minimum 8 characters" />
          </div>
          {error && <p className="text-sm text-red-400" data-testid="register-error">{error}</p>}
          <Button type="submit" disabled={loading} data-testid="register-submit-btn"
            className="w-full rounded-full bg-neon text-ink font-bold hover:bg-neon/90 h-12 text-sm transition-transform hover:scale-[1.01]">
            {loading ? "Creating account…" : "Create account"} <ArrowUpRight className="h-4 w-4 ml-2" />
          </Button>
          <p className="text-[11px] text-slate-500 leading-relaxed" data-testid="register-legal-notice">
            By creating an account you agree to our{" "}
            <Link to="/terms" className="text-neon hover:underline">Terms of Service</Link> and acknowledge our{" "}
            <Link to="/privacy" className="text-neon hover:underline">Privacy Policy</Link>.
          </p>
        </form>

        <div className="mt-6 flex items-center gap-3" aria-hidden="true">
          <span className="h-px flex-1 bg-white/10" />
          <span className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">or</span>
          <span className="h-px flex-1 bg-white/10" />
        </div>
        <div className="mt-6">
          <GoogleSignInButton label="Sign up with Google" />
        </div>

        <p className="mt-8 text-sm text-slate-500">
          Already have an account?{" "}
          <Link to="/login" className="text-neon font-semibold hover:underline" data-testid="register-login-link">Sign in</Link>
        </p>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }} className="mt-6 text-[11px] text-slate-600 max-w-md leading-relaxed">
          B2B professional accounts only. Please use a de-identified patient reference (initials + chart number) rather than full patient names in cases.
        </motion.p>
      </div>
    </div>
  );
}
